import { create } from "zustand";
import { persist } from "zustand/middleware";

import { IErrorStore } from "./types";

const updateStateForError = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  key: FieldKey,
  slug: FlowSlug,
  message: string,
) => {
  const newValidated = { ...state.validatedFields };
  if (newValidated[slug]) {
    newValidated[slug] = newValidated[slug]!.filter((k) => k !== key);
  }

  return {
    errors: {
      ...state.errors,
      [key]: message,
    },
    validatedFields: newValidated,
  };
};

const updateStateForClearError = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  key: FieldKey,
  slug: FlowSlug,
) => {
  const newErrors = { ...state.errors };
  delete newErrors[key];

  const newValidated = { ...state.validatedFields };
  const current = new Set(newValidated[slug] || []);
  current.add(key);
  newValidated[slug] = Array.from(current);

  return {
    errors: newErrors,
    validatedFields: newValidated,
  };
};

const updateStateForClearPageError = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  slug: FlowSlug,
) => {
  const newPageErrors = { ...state.pageErrors };
  delete newPageErrors[slug];
  return { pageErrors: newPageErrors };
};

const updateStateForSetPageError = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  slug: FlowSlug,
  message: string,
) => ({ pageErrors: { ...state.pageErrors, [slug]: message } });

const updateStateForRemoveValidatedFields = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  keys: FieldKey[],
  slug: FlowSlug,
) => {
  const newValidated = { ...state.validatedFields };

  // Only proceed if the slug exists in validatedFields
  if (newValidated[slug]) {
    // Create a Set for O(1) lookup performance
    const keysToRemove = new Set(keys);
    newValidated[slug] = newValidated[slug]!.filter((k) => !keysToRemove.has(k));
  }

  return {
    validatedFields: newValidated,
  };
};

export const createErrorStore = <
  FieldKey extends string,
  FlowSlug extends string,
>(
  storeName: string,
) => create<IErrorStore<FieldKey, FlowSlug>>()(
  persist(
    (set, get) => ({
      errors: {},
      pageErrors: {},
      validatedFields: {},

      setError: (key, slug, message) => {
        set((state) => updateStateForError(state, key, slug, message));
      },

      setPageError: (slug, message) => {
        set((state) => updateStateForSetPageError(state, slug, message));
      },

      getError: (key) => get().errors[key],

      getPageError: (slug: FlowSlug) => get().pageErrors[slug],

      clearError: (key, slug) => {
        set((state) => updateStateForClearError(state, key, slug));
      },

      clearPageError: (slug: FlowSlug) => {
        set((state) => updateStateForClearPageError(state, slug));
      },

      removeValidatedFields: (keys, slug) => {
        set((state) => updateStateForRemoveValidatedFields(state, keys, slug));
      },

      clearAll: () => {
        set({
          errors: {},
          validatedFields: {},
          pageErrors: {},
        });
      },
    }),
    { name: storeName },
  ),
);
